import { useCallback, useMemo, useState } from "react";
import { useDataProvider, useNotify, useRedirect } from "react-admin";
import { FieldValues } from "react-hook-form";
import { getRawUrlBase, PlanningResourcesEnum, RteCommunicationV2ResourcesEnum } from "shared/enums/resourceMappings";
import useAxiosFetch from "shared/hooks/useAxiosFetch";
import { fetchGroup, postIecConfigs, postPlannings } from "../querys";
import {
  formatIecConfigs,
  generateRackPlannings,
  getChronicleFilenameChoices,
  PLANNING_CONFIGS,
  preparePlannings,
} from "../utils";

const useTestsAfrrRteForm = () => {
  const [errors, setErrors] = useState<string[]>([]);
  const [planningsPosted, setPlanningsPosted] = useState<boolean>(false);
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const notify = useNotify();

  const iecFetchConfig = useMemo(
    () => ({
      url: `${getRawUrlBase(RteCommunicationV2ResourcesEnum.RteCommunicationTestConfig)}file-list`,
      method: "get" as const,
    }),
    [],
  );

  const {
    data: iecConfigs,
    loading: iecConfigsLoading,
    error: iecConfigsError,
  } = useAxiosFetch<string[]>(iecFetchConfig);

  const iecConfigChoices = useMemo(() => {
    return getChronicleFilenameChoices(iecConfigs);
  }, [iecConfigs]);

  const handleSubmit = useCallback(
    async (values: FieldValues) => {
      setErrors([]);
      setPlanningsPosted(false);
      const { group_id } = values;

      const group = await fetchGroup(group_id, dataProvider);
      if ("error" in group) {
        setErrors((prevState) => [...prevState, "Could not fetch group"]);
        return;
      }

      const formValues = {
        ...values,
        group,
      };

      const { sites } = group;
      const preparedSites: { id: number; rack_count: number }[] = sites.map(({ id, rack_count }) => ({
        id,
        rack_count,
      }));

      const planningsByRack = generateRackPlannings(formValues, PLANNING_CONFIGS);
      const preparedData = preparePlannings(planningsByRack, preparedSites);

      try {
        await postPlannings(preparedData);
        setPlanningsPosted(true);
      } catch (error) {
        setErrors((prevState) => [...prevState, "Failed to post plannings"]);
        notify("Failed to post plannings", { type: "error" });
        return; // interrupt flow
      }
      try {
        if (!iecConfigs || iecConfigs.length === 0) {
          throw new Error(
            "The plannings were correctly posted but no chronicle filenames could be retreived. Please check the IEC API.",
          );
        }
        const formattedIecConfigs = formatIecConfigs(values, planningsByRack);
        const postIecConfigsResult = await postIecConfigs(formattedIecConfigs, dataProvider);
        const hasIecFailed = postIecConfigsResult.some((result) => result.status === "rejected");
        if (hasIecFailed) {
          throw new Error("Plannings were correctly posted but failed to post IEC configs");
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : "Failed to post IEC configs";
        setErrors((prevState) => [...prevState, message]);
        notify(message, { type: "warning" });
        return; // interrupt flow
      }

      notify("Plannings and IEC configs posted successfully", { type: "success" });
      redirect(`/${PlanningResourcesEnum.Plannings}`);
    },
    [dataProvider, redirect, setErrors, notify, iecConfigs],
  );

  const iecConfigInfo = useMemo(
    () => ({
      iecConfigChoices,
      iecConfigsLoading,
      iecConfigsError,
    }),
    [iecConfigChoices, iecConfigsLoading, iecConfigsError],
  );

  return { handleSubmit, errors, iecConfigInfo, planningsPosted };
};

export default useTestsAfrrRteForm;
